import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../../components/brand/Logo.jsx'
import Button from '../../components/ui/Button.jsx'
import { Field } from '../../components/ui/Field.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { useAuth } from '../../context/AuthContext.jsx'

/*
 * Login — DESIGN.md §B. Centred card (max 430px), logo above, title "Bon retour",
 * email + password (48px, radius 12, bg #FBFAFF), forgot-password link, full-width
 * primary, "ou" divider, link to registration. Radial violet gradient background.
 */
const authInput = {
  width: '100%', height: 48, padding: '0 15px',
  borderRadius: 12, border: '1.5px solid var(--color-border-strong)',
  background: 'var(--color-bg-field)', fontSize: 14.5, color: 'var(--color-ink)',
}

export default function LoginPage() {
  const { t, i18n } = useTranslation()
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const result = await login(email, password)
      // Switch the UI to the user's preferred language on login.
      const lang = result?.user?.preferred_language
      if (lang && ['fr', 'en', 'nl'].includes(lang) && i18n.resolvedLanguage !== lang) i18n.changeLanguage(lang)
      if (result?.incomplete) {
        // Registration not finished: go to the waiting screen (under validation)
        // or back to the wizard to resume earlier steps.
        navigate(result.onboarding?.current_step === 'pending_validation' ? '/app/company-pending-validation' : '/register')
      } else {
        navigate('/app/dashboard')
      }
    } catch (err) {
      const status = err?.response?.status
      setError(status === 401 ? t('auth.login.invalidCredentials') : t('auth.login.genericError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      background: 'radial-gradient(1200px 600px at 80% -10%,#efe9fb,#f6f4fb)',
    }}>
      <div style={{ position: 'absolute', top: 22, right: 22 }}>
        <LanguageSwitch />
      </div>
      <div style={{ width: '100%', maxWidth: 430 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={34} onClick={() => navigate('/')} />
        </div>
        <div style={{
          background: '#fff', borderRadius: 'var(--radius-card-lg)',
          border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-card)',
          padding: '32px 30px', animation: 'vfade .25s ease',
        }}>
          <p style={{ textAlign: 'center', color: 'var(--color-text)', fontSize: 15, marginBottom: 24 }}>
            {t('auth.login.subtitle')}
          </p>

          {error && (
            <div role="alert" style={{
              background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
              border: '1px solid #f3c9c9', borderRadius: 10, padding: '10px 13px',
              fontSize: 13, fontWeight: 500, marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={onSubmit}>
            <Field label={t('auth.login.email')} htmlFor="email">
              <input id="email" type="email" autoComplete="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} style={authInput} placeholder="vous@societe.be" />
            </Field>
            <Field label={t('auth.login.password')} htmlFor="password">
              <input id="password" type="password" autoComplete="current-password" required value={password}
                onChange={(e) => setPassword(e.target.value)} style={authInput} placeholder="••••••••" />
            </Field>
            <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 16 }}>
              <Link to="/forgot-password" style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)' }}>
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
            <Button type="submit" variant="primary" fullWidth loading={submitting}>
              {t('auth.login.submit')}
            </Button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 18px' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('auth.login.or')}</span>
            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
          </div>

          <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--color-text)' }}>
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
              {t('auth.login.createAccount')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
